import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { listProjects, createProject as createProjectService, deleteProject as deleteProjectService, generatePoster } from "@/services/projectService";
import { getMyInvites, acceptInvite } from "@/services/teamService";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Trash2, User, LogOut, ChevronRight, Folder, Film, Sparkles, Clock, Clapperboard, Users, Mail, CheckCircle2, Shield, Bell } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/contexts/NotificationContext";
import { Logo1895 } from "@/components/Logo1895";
import NotificationPanel from "@/components/NotificationPanel";
import { IconSketchBadge, IconConceptBadge, IconPrevisBadge } from "@/components/Icons";
import CineIcon from "@/components/ui/CineIcon";
import SplashLoader from "@/components/SplashLoader";
import { DashboardSkeleton } from "@/components/Skeletons";
import { trackEvent, trackError, EventTypes, Categories, ErrorTypes } from "@/utils/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Premium project card with image preview
function ProjectCard({ project, onClick, onDelete }) {
  const stageColors = {
    sketch: 'from-slate-500/20 to-gray-600/20',
    concept: 'from-amber-500/20 to-orange-600/20',
    previs: 'from-cyan-500/20 to-teal-600/20',
    // Legacy values
    photorealistic: 'from-cyan-500/20 to-teal-600/20',
    drawing: 'from-slate-500/20 to-gray-600/20',
    anime: 'from-pink-500/20 to-purple-600/20',
    noir: 'from-zinc-500/20 to-neutral-800/20',
    watercolor: 'from-amber-500/20 to-orange-600/20',
    comic: 'from-red-500/20 to-yellow-600/20',
  };

  const bgGradient = stageColors[project.storyboard_style] || stageColors.sketch;

  return (
    <motion.div
      whileHover={{ y: -8, scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="card card-interactive p-0 group"
      data-testid={`project-card-${project.id}`}
    >
      {/* Thumbnail Area */}
      <div className={`aspect-[16/10] relative overflow-hidden ${project.thumbnail ? '' : `bg-gradient-to-br ${bgGradient}`}`}>
        {project.thumbnail ? (
          <img src={`data:image/jpeg;base64,${project.thumbnail}`} alt={project.name} className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-20 h-20 rounded-2xl bg-[rgba(31,31,35,0.5)] flex items-center justify-center backdrop-blur-sm">
              <Film size={36} className="text-[var(--text-disabled)]" />
            </div>
          </div>
        )}
        
        {/* Status Badge */}
        <div className="absolute top-4 right-4 flex gap-1.5">
          {project._shared && (
            <span className="chip bg-sky-500/20 text-sky-300 border-sky-500/30">
              <Users size={10} className="mr-1" />
              {project._my_role?.replace('_', ' ') || 'Shared'}
            </span>
          )}
          <span className="chip chip-gold flex items-center gap-1">
            {project.storyboard_style === 'sketch' ? <><IconSketchBadge size={10} /> Sketch</> :
             project.storyboard_style === 'concept' ? <><IconConceptBadge size={10} /> Concept</> :
             project.storyboard_style === 'previs' ? <><IconPrevisBadge size={10} /> Previs</> :
             'Cinematic'}
          </span>
        </div>
        
        {/* Delete Button (only for owned projects) */}
        {!project._shared && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(project.id); }}
          className="absolute top-4 left-4 w-9 h-9 rounded-lg bg-[rgba(9,9,11,0.6)] backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-[rgba(244,63,94,0.2)] text-[var(--text-muted)] hover:text-[var(--color-error)]"
          data-testid={`delete-project-btn-${project.id}`}
        >
          <Trash2 size={16} />
        </button>
        )}
        
        {/* Bottom Gradient */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[var(--bg-elevated)] to-transparent" />
      </div>
      
      {/* Info */}
      <div className="p-5">
        <h3 className="font-semibold text-[var(--text-primary)] text-lg mb-2 truncate group-hover:text-[var(--color-gold-400)] transition-colors">
          {project.name}
        </h3>
        
        {project.description && (
          <p className="text-sm text-[var(--text-muted)] line-clamp-2 mb-4">
            {project.description}
          </p>
        )}
        
        <div className="flex items-center justify-between pt-4 border-t border-[var(--border-subtle)]">
          <div className="flex items-center gap-2 text-xs text-[var(--text-disabled)]">
            <Clock size={12} />
            <span>
              {new Date(project.created_at).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric'
              })}
            </span>
          </div>
          
          <div className="flex items-center gap-1 text-xs text-[var(--text-muted)] group-hover:text-[var(--color-gold-400)] transition-colors">
            <span>Open</span>
            <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Quick start templates
function TemplateCard({ title, description, icon: Icon, onClick }) {
  return (
    <motion.button
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="glass-light rounded-2xl p-6 text-left hover:border-[rgba(212,168,83,0.3)] transition-all duration-300 group"
    >
      <CineIcon icon={Icon} size={48} iconSize={24} active className="mb-4" />
      <h4 className="font-semibold text-[var(--text-primary)] mb-1">{title}</h4>
      <p className="text-xs text-[var(--text-muted)]">{description}</p>
    </motion.button>
  );
}

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [newProject, setNewProject] = useState({ name: "", description: "", storyboard_style: "sketch" });
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  useEffect(() => {
    fetchProjects();
    fetchInvites();
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await listProjects();
      setProjects(data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvites = async () => {
    try {
      const data = await getMyInvites();
      setPendingInvites(data || []);
    } catch { /* ignore — user may have no invites */ }
  };

  const handleAcceptInvite = async (invite) => {
    try {
      const data = await acceptInvite(invite.invite_token);
      toast.success(data.message);
      setPendingInvites(prev => prev.filter(i => i.id !== invite.id));
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept");
    }
  };

  const createProject = async () => {
    if (!newProject.name.trim()) {
      toast.error("Project name is required");
      return;
    }
    if (!newProject.description.trim()) {
      toast.error("Project description is required for poster generation");
      return;
    }

    try {
      const project = await createProjectService(newProject);
      setProjects([project, ...projects]);
      setNewProject({ name: "", description: "", storyboard_style: "sketch" });
      setShowCreateDialog(false);
      toast.success("Project created — generating poster...");
      
      trackEvent(EventTypes.PROJECT_CREATE, Categories.PROJECT, {
        style: newProject.storyboard_style
      });
      
      navigate(`/project/${project.id}`);
      
      generatePoster(project.id)
        .then(() => toast.success("Movie poster generated!"))
        .catch(() => {});
    } catch (error) {
      console.error("Error creating project:", error);
      toast.error("Failed to create project");
      trackError(ErrorTypes.API_ERROR, error, 'create_project');
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm("Delete this project? This cannot be undone.")) return;

    try {
      await deleteProjectService(projectId);
      setProjects(projects.filter(p => p.id !== projectId));
      toast.success("Project deleted");
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    }
  };

  return (
    <div className="h-screen flex flex-col bg-[var(--bg-app)] text-[var(--text-primary)] overflow-hidden">
      {/* Premium Header */}
      <header className="border-b border-[var(--border-subtle)] bg-[rgba(17,17,19,0.8)] backdrop-blur-xl sticky top-0 z-50 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-4"
          >
            <Logo1895 variant="icon" width={36} />
            <div>
              <h1 className="text-lg font-semibold tracking-tight" style={{ fontFamily: "'Palatino Linotype','Book Antiqua',Palatino,serif" }}>
                <span style={{ color: '#DAA520' }}>1895</span>
              </h1>
            </div>
          </motion.div>
          
          <div className="flex items-center gap-3">
            {/* Notification Bell */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowNotifications(v => !v)}
              className="relative p-2 rounded-lg hover:bg-[var(--bg-elevated)] transition-colors"
              title="Notifications"
            >
              <Bell size={20} className="text-[var(--text-secondary)]" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </motion.button>

            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button data-testid="create-project-btn" className="btn-primary cine-shimmer">
                    <Plus size={18} />
                    <span className="ml-2 hidden sm:inline">New Project</span>
                  </Button>
                </motion.div>
              </DialogTrigger>
              <DialogContent className="bg-[var(--bg-surface)] border-[var(--border-default)] text-[var(--text-primary)] backdrop-blur-xl rounded-2xl max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-xl font-semibold flex items-center gap-3">
                    <CineIcon icon={Sparkles} size={40} iconSize={20} active />
                    Create Project
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-5 mt-6">
                  <div>
                    <Label className="label">Project Name</Label>
                    <Input
                      data-testid="project-name-input"
                      value={newProject.name}
                      onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                      className="input mt-2"
                      placeholder="My Feature Film"
                      onKeyDown={(e) => e.key === 'Enter' && createProject()}
                    />
                  </div>
                  <div>
                    <Label className="label">
                      Description <span className="text-[var(--color-error)]">*</span>
                    </Label>
                    <Textarea
                      data-testid="project-description-input"
                      value={newProject.description}
                      onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                      className="input mt-2 resize-none h-24"
                      placeholder="A brief synopsis of your project — this will be used to generate the movie poster thumbnail..."
                    />
                    <p className="text-[11px] text-[var(--text-disabled)] mt-1">Used to auto-generate a movie poster as project thumbnail</p>
                  </div>
                  <div>
                    <Label className="label">Default Storyboard Stage</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {[
                        { value: "sketch", Icon: IconSketchBadge, label: "Director's Sketch", desc: "Blocking & composition" },
                        { value: "concept", Icon: IconConceptBadge, label: "Concept Art", desc: "Color & atmosphere" },
                        { value: "previs", Icon: IconPrevisBadge, label: "Previsualization", desc: "Photorealistic reference" },
                      ].map((s) => (
                        <button
                          key={s.value}
                          type="button"
                          onClick={() => setNewProject({ ...newProject, storyboard_style: s.value })}
                          className={`p-2.5 rounded-lg border text-left transition-all ${
                            newProject.storyboard_style === s.value
                              ? 'border-[var(--color-gold-500)] bg-[rgba(212,168,83,0.1)]'
                              : 'border-[var(--border-subtle)] bg-[var(--bg-muted)] hover:border-[var(--border-default)]'
                          }`}
                        >
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <s.Icon size={14} />
                            <span className="text-xs font-medium text-[var(--text-primary)]">{s.label}</span>
                          </div>
                          <p className="text-[11px] text-[var(--text-muted)]">{s.desc}</p>
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-[var(--text-disabled)] mt-1">You can change this per-generation later</p>
                  </div>
                  <Button
                    data-testid="create-project-submit-btn"
                    onClick={createProject}
                    className="btn-primary cine-shimmer w-full py-5"
                  >
                    Create Project
                    <ChevronRight size={18} className="ml-2" />
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-3 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-all duration-200 rounded-xl px-3 h-10"
                  data-testid="user-menu-btn"
                >
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-lg ring-2 ring-[var(--border-default)]" />
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-[var(--bg-overlay)] flex items-center justify-center">
                      <User size={18} />
                    </div>
                  )}
                  <span className="text-sm font-medium hidden sm:block">{user?.name?.split(' ')[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[var(--bg-surface)] border-[var(--border-default)] text-[var(--text-primary)] backdrop-blur-xl rounded-xl min-w-[200px]" align="end">
                <div className="px-3 py-2 border-b border-[var(--border-subtle)]">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-[var(--text-muted)]">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-[var(--border-subtle)]" />
                <DropdownMenuItem
                  onClick={() => navigate('/settings/vault')}
                  className="focus:bg-[var(--bg-hover)] cursor-pointer rounded-lg mx-1 my-1"
                >
                  <Shield size={16} className="mr-2" />
                  Vault &amp; Security
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[var(--border-subtle)]" />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-[var(--color-error)] focus:text-[var(--color-error)] focus:bg-[var(--bg-hover)] cursor-pointer rounded-lg mx-1 my-1"
                  data-testid="logout-btn"
                >
                  <LogOut size={16} className="mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content - Scrollable */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">

        {/* Pending Invites Banner */}
        {pendingInvites.length > 0 && (
          <div className="mb-8" data-testid="pending-invites-section">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
              <Mail size={16} className="text-[var(--color-gold-400)]" />
              Pending Invitations
            </h3>
            <div className="space-y-2">
              {pendingInvites.map(invite => (
                <motion.div
                  key={invite.id}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between p-4 rounded-xl bg-[rgba(212,168,83,0.05)] border border-[rgba(212,168,83,0.2)]"
                  data-testid={`invite-banner-${invite.id}`}
                >
                  <div className="flex items-center gap-3">
                    <CineIcon icon={Film} size={40} iconSize={18} active />

                    <div>
                      <p className="text-sm font-medium text-[var(--text-primary)]">
                        {invite.inviter_name} invited you to <strong>{invite.project_name}</strong>
                      </p>
                      <p className="text-xs text-[var(--text-muted)]">
                        Role: {invite.role_label}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleAcceptInvite(invite)}
                    className="btn-primary text-xs h-8"
                    size="sm"
                    data-testid={`accept-invite-${invite.id}`}
                  >
                    <CheckCircle2 size={14} className="mr-1" /> Accept
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <DashboardSkeleton />
        ) : projects.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center pt-16"
          >
            {/* Empty State Hero */}
            <motion.div 
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="w-28 h-28 rounded-3xl bg-gradient-to-br from-[rgba(212,168,83,0.15)] to-[rgba(184,146,58,0.1)] flex items-center justify-center mx-auto mb-10 border border-[var(--border-default)] shadow-xl"
            >
              <Clapperboard size={48} className="text-[var(--color-gold-400)]" />
            </motion.div>
            
            <h2 className="h2 text-[var(--text-primary)] mb-4">Start Your First Project</h2>
            <p className="text-[var(--text-muted)] mb-12 max-w-md mx-auto text-base leading-relaxed">
              Transform your screenplay into a visual storyboard with AI-powered generation.
            </p>
            
            {/* Quick Start Templates */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto mb-12">
              <TemplateCard
                title="Feature Film"
                description="Full-length narrative"
                icon={Film}
                onClick={() => setShowCreateDialog(true)}
              />
              <TemplateCard
                title="Short Film"
                description="Under 40 minutes"
                icon={Sparkles}
                onClick={() => setShowCreateDialog(true)}
              />
              <TemplateCard
                title="Commercial"
                description="Ad or promo"
                icon={Folder}
                onClick={() => setShowCreateDialog(true)}
              />
            </div>
            
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                data-testid="empty-create-project-btn"
                onClick={() => setShowCreateDialog(true)}
                className="btn-primary cine-shimmer text-base px-8 py-6"
              >
                <Plus size={20} className="mr-2" />
                Create Your First Project
              </Button>
            </motion.div>
          </motion.div>
        ) : (
          <>
            {/* Hero Greeting */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-10"
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--color-gold-500)] mb-2">Workspace</div>
              <h1 className="text-3xl font-semibold text-[var(--text-primary)] tracking-tight">
                {new Date().getHours() < 12 ? 'Good morning' : new Date().getHours() < 17 ? 'Good afternoon' : 'Good evening'}, {user?.name?.split(' ')[0] || 'there'}
              </h1>
              <p className="text-[var(--text-muted)] mt-2 text-sm">
                {projects.length} project{projects.length !== 1 ? 's' : ''} — {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
            </motion.div>

            {/* Project Grid */}
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              initial="hidden"
              animate="visible"
              variants={{
                visible: { transition: { staggerChildren: 0.08 } }
              }}
            >
              {projects.map((project) => (
                <motion.div
                  key={project.id}
                  variants={{
                    hidden: { opacity: 0, y: 30 },
                    visible: { opacity: 1, y: 0 }
                  }}
                >
                  <ProjectCard
                    project={project}
                    onClick={() => navigate(`/project/${project.id}`)}
                    onDelete={deleteProject}
                  />
                </motion.div>
              ))}
            </motion.div>
          </>
        )}
        </div>
      </main>

      {/* Notification Slide-over */}
      <NotificationPanel
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
    </div>
  );
}
